import { z } from "zod"

/**
 * Signed-in devices (T41). A device is one Supabase Auth session (one refresh-token family): the
 * server lists them and revokes them for the signed-in user, from the web and from the native
 * apps alike. Request schemas are strict; response schemas are lenient (no limits, no narrow
 * enums) so a newer server field never breaks an older client.
 *
 * Revoking a session stops it from refreshing; an access token it already holds keeps working
 * until it expires (at most the project's JWT lifetime; 3600 s in the local config, hosted value not verified). A client must therefore report
 * success only from a 2xx answer with `remaining` as expected, never from its own sign-out call.
 */

/** `others` signs out every session except the one making the request; `all` includes it. */
export const revokeSessionsScopeSchema = z.enum(["others", "all"])
export type RevokeSessionsScope = z.infer<typeof revokeSessionsScopeSchema>

/** `DELETE /api/v1/sessions?scope=others|all` (default `others`). */
export const revokeSessionsQuerySchema = z.strictObject({
  scope: revokeSessionsScopeSchema.default("others"),
})
export type RevokeSessionsQuery = z.input<typeof revokeSessionsQuerySchema>

export const sessionSchema = z.object({
  id: z.string(),
  /** What the server could tell about the device from the sign-in, e.g. `Chrome on Mac` or `Android app`. Never an address or an exact model. */
  device_label: z.string().default("Unknown device"),
  created_at: z.string(),
  /** Last time the session was used to refresh its tokens; null when the server does not know. */
  last_active_at: z.string().nullable().default(null),
  /** True for the session that made this request. */
  current: z.boolean().default(false),
})
export type Session = z.infer<typeof sessionSchema>

export const sessionListSchema = z.object({ sessions: z.array(sessionSchema) })
export type SessionList = z.infer<typeof sessionListSchema>

/**
 * Answer of a bulk revoke. The server checks the outcome before it answers 200: `remaining` is how
 * many sessions the user still has (0 for `all`, 1 for `others`). Anything that did not happen is a
 * non-2xx error, never a 200 with a hopeful count.
 */
export const revokeSessionsResultSchema = z.object({
  scope: z.string(),
  revoked: z.number(),
  remaining: z.number(),
})
export type RevokeSessionsResult = z.infer<typeof revokeSessionsResultSchema>

/** `details.reason` of a 409 CONFLICT on `scope=others` when the request carries no session id. */
export const CURRENT_SESSION_UNKNOWN_REASON = "current_session_unknown"
